/**
 * A clean multi-column sort utility that doesn't depend on ColumnDefinition.
 * Used for sorting data outside of TanStack tables (e.g., PurchaseOrder display).
 */

import { cacheByItem } from './cache-by-item';

export type Comparator<T> = (a: T, b: T) => number;

type SortSpec<T> = {
  /** Function to extract the sortable value from an item */
  accessor: (item: T) => string | number;
  /** Sort direction */
  direction: 'asc' | 'desc';
  /** Optional custom compare function for complex comparisons */
  compare?: (a: T, b: T) => number;
};

/**
 * Creates a comparison function for multi-column sorting.
 *
 * @example
 * const compare = multiColumnSort<Product>([
 *   { accessor: (p) => p.department, direction: 'desc' },
 *   { accessor: (p) => p.brand, direction: 'asc' },
 *   { accessor: (p) => p.name, direction: 'asc' },
 * ]);
 * const sorted = products.sort(compare);
 */
export function multiColumnSort<T extends object>(specs: SortSpec<T>[]): Comparator<T> {
  // Each spec becomes a comparator with its direction already folded in, so the
  // per-comparison loop below is just multiSort's fold — no need to re-derive
  // direction, or to index a parallel array, on every comparison.
  //
  // The key cache lives as long as the returned comparator, and callers memoize
  // it and then sort with it more than once — review-order sorts the selected
  // items and again for display — so the second sort reuses the first's keys.
  const comparators = specs.map(({ accessor, direction, compare }): Comparator<T> => {
    // `??` short-circuits, so a spec with its own compare never builds a cache.
    const ascending = compare ?? byKey(cacheByItem(accessor));

    return direction === 'asc' ? ascending : (a, b) => -ascending(a, b);
  });

  return multiSort(...comparators);
}

/** Compare by a derived key: numbers numerically, everything else by locale. */
function byKey<T>(key: (item: T) => string | number): Comparator<T> {
  return (a, b) => {
    const aVal = key(a);
    const bVal = key(b);

    return typeof aVal === 'number' && typeof bVal === 'number'
      ? aVal - bVal
      : String(aVal).localeCompare(String(bVal));
  };
}

/** Compose comparators; the first non-zero result decides the order. */
export function multiSort<T>(...comparators: Comparator<T>[]): Comparator<T> {
  return (a, b) => {
    // Indexed rather than for..of: this runs once per comparison, and an
    // array iterator per call is pure GC churn on a sort's hot path.
    for (let i = 0; i < comparators.length; i++) {
      const result = comparators[i](a, b);
      if (result !== 0) {
        return result;
      }
    }

    return 0;
  };
}

/** Compare two ordered values; nullish values sort to the end. Strings use locale-aware ordering. */
export function compare<T extends string | number | Date>(
  a: T | null | undefined,
  b: T | null | undefined
): 1 | 0 | -1 {
  if (a === b) {
    return 0;
  }

  if (a === null || a === undefined) {
    return 1;
  }

  if (b === null || b === undefined) {
    return -1;
  }

  if (typeof a === 'string' && typeof b === 'string') {
    const cmp = a.localeCompare(b);

    return cmp === 0 ? 0 : cmp < 0 ? -1 : 1;
  }

  return a < b ? -1 : 1;
}
